import React, { useId, useLayoutEffect, useRef, useState } from 'react'
import { css } from '@emotion/react'
import { createPortal } from 'react-dom'

const ellipsisTdStyle = css`
  position: relative;
  white-space: nowrap;
  text-overflow: ellipsis;
  outline: none;
  box-sizing: border-box;
`

const ellipsisTdFocusStyle = css`
  box-shadow: inset 0 0 0 2px #4d90fe;
`

const ellipsisTdTextStyle = css`
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
`

const ellipsisTdTooltipStyle = css`
  position: fixed;
  background-color: #333333;
  color: #ffffff;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  white-space: pre-wrap;
  width: max-content;
  z-index: 10000;
  pointer-events: none;
  max-width: min(500px, calc(100vw - 16px));
  box-sizing: border-box;
  overflow-wrap: anywhere;

  --arrow-left: 50%;

  &[data-placement='bottom'] {
    &::after {
      top: auto;
      bottom: 100%;
      border-top-color: transparent;
      border-bottom-color: #333333;
    }
  }

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: var(--arrow-left);
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #333333;
  }
`
type EllipsisTdProps = {
  text: string
  className?: string
  tooltipClassName?: string
  divStyle?: React.CSSProperties
  width?: string
}

type Props = EllipsisTdProps & React.ComponentProps<'td'>

export const EllipsisTd: React.FC<Props> = ({
  text,
  className = '',
  tooltipClassName = '',
  divStyle = {},
  width = '100%',
  style,
  ...props
}) => {
  const [open, setOpen] = useState(false)
  const [isTruncated, setIsTruncated] = useState(false)
  const [tooltipPlacement, setTooltipPlacement] = useState<'top' | 'bottom'>(
    'top',
  )
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({})
  const tooltipId = useId()
  const tdRef = useRef<HTMLTableCellElement>(null)
  const textRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!textRef.current) {
      setIsTruncated(false)
      return
    }

    const measureTruncation = () => {
      if (!textRef.current) return

      setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth)
    }

    measureTruncation()

    const resizeObserver = new ResizeObserver(measureTruncation)
    resizeObserver.observe(textRef.current)
    if (tdRef.current) {
      resizeObserver.observe(tdRef.current)
    }

    window.addEventListener('resize', measureTruncation)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', measureTruncation)
    }
  }, [text, width, divStyle])

  useLayoutEffect(() => {
    if (!open || !isTruncated || !tdRef.current || !tooltipRef.current) {
      setTooltipPlacement('top')
      setTooltipStyle({})
      return
    }

    const adjustTooltipPosition = () => {
      if (!tdRef.current || !tooltipRef.current) return

      const viewportPadding = 8
      const tooltipGap = 8
      const arrowPadding = 12
      const tdRect = tdRef.current.getBoundingClientRect()
      const tooltipRect = tooltipRef.current.getBoundingClientRect()

      const spaceAbove = tdRect.top - viewportPadding
      const spaceBelow = window.innerHeight - tdRect.bottom - viewportPadding

      const nextPlacement =
        tooltipRect.height <= spaceAbove || spaceAbove >= spaceBelow
          ? 'top'
          : 'bottom'

      const desiredTopInViewport =
        nextPlacement === 'top'
          ? tdRect.top - tooltipRect.height - tooltipGap
          : tdRect.bottom + tooltipGap

      const maxTopInViewport = Math.max(
        viewportPadding,
        window.innerHeight - viewportPadding - tooltipRect.height,
      )
      const clampedTopInViewport = Math.min(
        Math.max(desiredTopInViewport, viewportPadding),
        maxTopInViewport,
      )

      const desiredCenterX = tdRect.left + tdRect.width / 2
      const tooltipCenter = tooltipRect.width / 2
      const minCenterX = viewportPadding + tooltipCenter
      const maxCenterX = window.innerWidth - viewportPadding - tooltipCenter
      const clampedCenterX =
        minCenterX <= maxCenterX
          ? Math.min(Math.max(desiredCenterX, minCenterX), maxCenterX)
          : window.innerWidth / 2
      const arrowLeft = Math.min(
        Math.max(tooltipCenter + desiredCenterX - clampedCenterX, arrowPadding),
        tooltipRect.width - arrowPadding,
      )

      setTooltipPlacement(nextPlacement)
      setTooltipStyle({
        top: `${clampedTopInViewport}px`,
        left: `${clampedCenterX}px`,
        transform: 'translateX(-50%)',
        ['--arrow-left' as string]: `${arrowLeft}px`,
      })
    }

    adjustTooltipPosition()

    window.addEventListener('resize', adjustTooltipPosition)
    window.addEventListener('scroll', adjustTooltipPosition, true)

    return () => {
      window.removeEventListener('resize', adjustTooltipPosition)
      window.removeEventListener('scroll', adjustTooltipPosition, true)
    }
  }, [open, isTruncated, text])

  return (
    <td
      ref={tdRef}
      css={ellipsisTdStyle}
      className={className}
      style={{ width, maxWidth: width, ...style }}
      tabIndex={0}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      aria-describedby={open && isTruncated ? tooltipId : undefined}
      {...props}
    >
      <div
        ref={textRef}
        style={{ width: '100%', maxWidth: '100%', ...divStyle }}
        css={ellipsisTdTextStyle}
      >
        {text}
      </div>

      {open &&
        isTruncated &&
        createPortal(
          <div
            ref={tooltipRef}
            id={tooltipId}
            role="tooltip"
            data-placement={tooltipPlacement}
            className={tooltipClassName}
            css={ellipsisTdTooltipStyle}
            style={tooltipStyle}
          >
            {text}
          </div>,
          document.body,
        )}
    </td>
  )
}
